import { Alert, Paper } from "@mui/material";
import { type ReactElement, useMemo } from "react";
import { ChartSkeleton } from "./chart-skeleton";

interface ChartStateWrapperProps<T> {
  isLoading: boolean;
  data: T | undefined;
  error?: unknown;
  renderChart: (data: T) => ReactElement;
  title?: string;
  emptyMessage?: string;
  errorMessage?: string;
}

export function ChartStateWrapper<T>({
  isLoading,
  data,
  error,
  renderChart,
  title,
  emptyMessage = "No data available",
  errorMessage = "Error loading data",
}: ChartStateWrapperProps<T>) {
  const content = useMemo(() => {
    if (isLoading) {
      return <ChartSkeleton title={title} />;
    }

    if (error) {
      return (
        <Paper
          sx={{
            p: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Alert severity="info" sx={{ width: "100%" }}>
            {emptyMessage}
          </Alert>
        </Paper>
      );
    }

    if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
      return (
        <Paper
          sx={{
            p: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Alert severity="info" sx={{ width: "100%" }}>
            {emptyMessage}
          </Alert>
        </Paper>
      );
    }

    return renderChart(data);
  }, [isLoading, data, error, renderChart, title, emptyMessage, errorMessage]);

  return content;
}
